/**
 * The page's chat on a phone and an iPad: a plain chat with Flash. No preset layout, colour, words or order. + adds files
 * from the one From Assets picker, a screenshot dropped in as a reference still works, and a reply that changed the page
 * says so with a way back. What is said and sent is `useNoteComposer`, the same as the computer's card.
 */
import { type DragEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { PageAttachment, PageMessage } from "../../../shared/types";
import { Icon } from "../../components/ui/Icon";
import { cx } from "../../components/ui/cx";
import { FileChip, FileFace, useNoteComposer } from "../walk/WalkChat";

export interface ChatProps {
  thread: readonly PageMessage[];
  busy: boolean;
  onAsk: (text: string, files: PageAttachment[]) => Promise<boolean>;
  onBack: (messageId: string) => Promise<void> | void;
  placeholder: string;
  error?: string;
  /** A phone sends the host back to the page to see the change; an iPad has it beside the chat and holds Before. */
  phone: boolean;
  onSee?: () => void;
  onPeek?: (on: boolean) => void;
}

/** The thread: the host's words, Flash's replies with See it or Before and Undo, and Flash at work. */
export function Thread({ thread, busy, onBack, phone, onSee, onPeek }: Pick<ChatProps, "thread" | "busy" | "onBack" | "phone" | "onSee" | "onPeek">) {
  const log = useRef<HTMLDivElement>(null);
  const [holding, setHolding] = useState(false);
  // The newest message is always in view, on opening and after every reply.
  useLayoutEffect(() => {
    const el = log.current;
    if (el) el.scrollTop = el.scrollHeight;
    if (el && phone) window.scrollTo({ top: document.body.scrollHeight });
  }, [thread.length, busy, phone]);
  const hold = (on: boolean) => {
    if (on === holding) return;
    setHolding(on);
    onPeek?.(on);
  };
  const heldNow = useRef(holding);
  heldNow.current = holding;
  const peekNow = useRef(onPeek);
  peekNow.current = onPeek;
  useEffect(() => () => void (heldNow.current && peekNow.current?.(false)), []);
  const shown = thread.filter((m) => !m.taken);
  const lastChange = shown.findLast((m) => m.who === "ai" && m.before !== undefined);
  return (
    <div className="mk-msgs" ref={log} aria-live="polite">
      {shown.length === 0 && !busy ? (
        <div className="mk-none">
          <Icon name="sparkle" />
        </div>
      ) : null}
      {shown.map((m) =>
        m.who === "me" ? (
          <div key={m.id} className="mk-m me">
            <div className="mk-mbub">
              {m.text}
              {m.files?.length ? (
                <div className="mk-mfiles">
                  {m.files.map((f) => (
                    <FileFace f={f} key={f.key} />
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <div key={m.id} className="mk-m fl">
            <span className="mk-flm" aria-hidden="true">
              <Icon name="sparkle" />
            </span>
            <div className="mk-mbub">
              {m.text}
              {m.before !== undefined ? (
                <div className="mk-macts">
                  {lastChange?.id === m.id ? (
                    <>
                      {phone ? (
                        <button type="button" className="btn" onClick={onSee}>
                          <Icon name="eye" /> See it
                        </button>
                      ) : (
                        <button
                          type="button"
                          className={cx("btn", holding && "on")}
                          title="Hold to see how it was"
                          onPointerDown={() => hold(true)}
                          onPointerUp={() => hold(false)}
                          onPointerLeave={() => hold(false)}
                          onPointerCancel={() => hold(false)}
                          onKeyDown={(e) => (e.key === " " || e.key === "Enter") && !e.repeat && hold(true)}
                          onKeyUp={() => hold(false)}
                          onBlur={() => hold(false)}
                        >
                          <Icon name="eye" /> Before
                        </button>
                      )}
                      <button type="button" className="btn" onClick={() => void onBack(m.id)}>
                        <Icon name="undo" /> Undo
                      </button>
                    </>
                  ) : (
                    <button type="button" className="btn" onClick={() => void onBack(m.id)}>
                      <Icon name="undo" /> Back to before this
                    </button>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        ),
      )}
      {busy ? (
        <div className="mk-m fl">
          <span className="mk-flm" aria-hidden="true">
            <Icon name="sparkle" />
          </span>
          <div className="mk-mbub mk-dots-w" aria-label="Flash is working">
            <i />
            <i />
            <i />
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** The box: + for files, a growing field that shows its whole question, and send. */
export function Composer({ onAsk, busy, placeholder, error }: Pick<ChatProps, "onAsk" | "busy" | "placeholder" | "error">) {
  const { text, setText, files, setFiles, said, attach, send, paste } = useNoteComposer(onAsk, busy);
  const [over, setOver] = useState(false);
  const box = useRef<HTMLTextAreaElement>(null);
  // Why on the text: the field grows with what is typed, up to a few lines, and shows all of its placeholder.
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(132, el.scrollHeight + 2)}px`;
  }, [text]);
  const drop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    const dropped = [...(e.dataTransfer?.files ?? [])];
    if (dropped.length) attach(dropped);
  };
  return (
    <div
      className={cx("mk-comp", over && "drop")}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={drop}
    >
      {error || said ? (
        <p className="mk-cerr" role="alert">
          {error || said}
        </p>
      ) : null}
      {files.length ? (
        <div className="mk-pend">
          {files.map((f, i) => (
            <FileChip key={f.key} f={f} onOff={() => setFiles((was) => was.filter((_, k) => k !== i))} />
          ))}
        </div>
      ) : null}
      <div className="mk-crow">
        <button type="button" className="mk-plus" aria-label="Add from Assets" onClick={() => attach()}>
          <Icon name="plus" />
        </button>
        <div className="mk-tw">
          <textarea dir="auto"
            ref={box}
            rows={1}
            value={text}
            placeholder={placeholder}
            aria-label={placeholder}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
            onPaste={paste}
          />
        </div>
        <button type="button" className="mk-sendi" aria-label="Send" disabled={busy || (!text.trim() && !files.length)} onClick={() => void send()}>
          <Icon name="send" />
        </button>
      </div>
    </div>
  );
}
